import React, { useState } from 'react'
import { useSeo } from '../../lib/i18n'

export default function AnbefalingerEn() {
  useSeo({
    title: 'Testimonials | Borgs Driving School Kalundborg | Student Reviews',
    description: 'Read reviews and testimonials from former students at Borgs Driving School in Kalundborg. See what students say about our driving lessons.',
    lang: 'en',
    pathname: '/en/testimonials'
  })

  const [formState, setFormState] = useState({
    name: '',
    email: '',
    message: '',
    website_url: '' // honeypot
  })
  const [submitted, setFormSubmitted] = useState(false)
  const [submitting, setFormSubmitting] = useState(false)
  const [error, setFormError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormSubmitting(true)
    setFormError('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState)
      })

      if (response.ok) {
        setFormSubmitted(true)
      } else {
        setFormError('Some fields are not filled in correctly')
      }
    } catch (err) {
      setFormError('An error occurred. Please try again.')
    } finally {
      setFormSubmitting(false)
    }
  }

  const reviews = [
    {
      author: 'Corné Engsvang',
      text: 'Had a really good experience with this course. Best driving school in Kalundborg! Really good theory lessons with Mette and a very good course driving with Kim.'
    },
    {
      author: 'Matilde Larsen',
      text: 'World’s best driving school! I would without a doubt recommend Borgs Driving School to anyone who asks! Mette and Kim are the most fantastic people.'
    },
    {
      author: 'Mohamed Gamal',
      text: 'Fantastic driving school. Pleasant driving instructors who even set aside more time for teaching than legally required. Theory is easy to understand, and overall a very great atmosphere 10/10.'
    },
    {
      author: 'Barbara Sabljak',
      text: 'I give my warmest recommendation to Mette and Kim. I am 52 years old and had to get my first license; it was a safe experience, Mette is eminent at teaching.'
    },
    {
      author: 'Lene Johansen',
      text: 'Super fantastic driving and theory instructors. I am so glad you could help me get my driver’s license.'
    },
    {
      author: 'Mathilde Knudsen',
      text: 'Fantastic driving instructor! I came from another driving school, but quickly got the support and learning I was missing. Highly recommended.'
    }
  ]

  return (
    <div className="bg-slate-50 min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Main Content Box */}
        <div className="bg-white p-8 sm:p-12 border border-slate-200 shadow-sm space-y-6">
          <h1 className="font-header text-3xl sm:text-4xl font-extrabold text-slate-900">
            Student Testimonials
          </h1>
          <p className="text-slate-700 text-base leading-relaxed">
            On this page, you can read about previous students&apos; experiences at Borgs Driving School throughout the years.
          </p>

          <section className="pt-6 border-t border-slate-200 space-y-4">
            <h2 className="font-header text-2xl font-bold text-slate-900">
              Did you have a great experience?
            </h2>
            <p className="text-slate-700">
              If you have been a student at Borgs Driving School and would like to share your review, you are more than welcome to use our form below and tell us about your experience.
            </p>

            {submitted ? (
              <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold rounded-lg">
                Thank you for your testimonial! We greatly appreciate your feedback.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
                {/* Honeypot field */}
                <div className="sr-only absolute -left-[9999px]">
                  <label htmlFor="website_url">Website</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex="-1"
                    autoComplete="off"
                    value={formState.website_url}
                    onChange={(e) =>
                      setFormState({ ...formState, website_url: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-slate-300 focus:border-red-600 focus:outline-none"
                    value={formState.name}
                    onChange={(e) =>
                      setFormState({ ...formState, name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-2 border border-slate-300 focus:border-red-600 focus:outline-none"
                    value={formState.email}
                    onChange={(e) =>
                      setFormState({ ...formState, email: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-1">
                    Your Review *
                  </label>
                  <textarea
                    rows={4}
                    required
                    className="w-full px-4 py-2 border border-slate-300 focus:border-red-600 focus:outline-none"
                    value={formState.message}
                    onChange={(e) =>
                      setFormState({ ...formState, message: e.target.value })
                    }
                  />
                </div>

                {error && (
                  <div className="text-red-600 text-sm font-bold">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 bg-red-600 text-white font-bold hover:bg-red-700 transition duration-150 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Testimonial'}
                </button>
              </form>
            )}
          </section>
        </div>

        {/* Reviews Listing */}
        <div className="space-y-6">
          <h2 className="font-header text-2xl font-bold text-slate-900 border-b border-slate-300 pb-2">
            Read Selected Reviews
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reviews.map((rev, index) => (
              <div
                key={index}
                className="bg-white p-6 border border-slate-200 shadow-sm flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex text-amber-400">
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                    <i className="fa-solid fa-star" />
                  </div>
                  <p className="text-slate-600 italic whitespace-pre-line text-sm leading-relaxed">
                    &quot;{rev.text}&quot;
                  </p>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100 font-bold text-slate-900 text-sm">
                  {rev.author}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
